import { BodyBox } from '@lbcamden/lib-react';
import { formatDMY, formatOrFallback, titleCaseFromCaps } from '@lbcamden/shared-utils';
import { FunctionComponent } from 'react';
import { useNavigate } from 'react-router';

import { personFieldDisplayValues } from '~/client/config/display-values/people';
import { useReferenceListDisplayValue } from '~/client/hooks/storeHooks';
import { useHandlePendingItem } from '~/client/hooks/useHandlePendingItem';
import {
  getPropertyBreadcrumbs,
  supportNeedsPersonPageUrl,
  toContactDetailsEdit,
  toPersonEditPage,
  toPersonPage,
  toPropertyPage,
} from '~/client/utils/navigation';
import { ContactDetailsItem, PersonAttributeItem } from '~/common/types/people';

import { InfoItem, InfoList, itemActionLabel, ItemDetails } from '../../common/ItemDetails';
import { Markdown } from '../../common/Markdown';
import { PageTitle } from '../../common/PageTitle';
import { PageBreadcrumbs } from '../../shared/PageBreadcrumbs';
import { LoadInNotFoundPage } from '../LoadInNotFoundPage';
import { PcfraStatusComments } from './PersonPage.styled';
import { useEndHouseholdMemberDialog } from './useEndHouseholdMemberDialog';
import { usePersonFromPath } from './usePersonFromPath';

const titleLabel = (isTenant: boolean, isPendingMember?: boolean) =>
  isTenant ? 'Current tenant' : `Household member${isPendingMember ? ' (pending)' : ''}`;

export const PersonPage: FunctionComponent = () => {
  const navigate = useNavigate();

  const { property, person } = usePersonFromPath();
  const referenceListDisplayValue = useReferenceListDisplayValue();

  const { showEndMemberDialog, endMemberDialog } = useEndHouseholdMemberDialog(person);

  const { isPendingItem: isPendingMember } = useHandlePendingItem(
    person?.personId,
    property ? toPropertyPage(property.propertyId) : '',
    (realId) => toPersonPage(property?.propertyId ?? 0, realId),
  );

  if (!property || !person) {
    return <LoadInNotFoundPage />;
  }

  const { propertyId, personId, isTenant, isHistoric } = person;
  const name = person?.fullName;

  const onClickUpdate = () => navigate(toPersonEditPage(propertyId, personId));

  const onClickOpenSupportNeeds = () => window.open(supportNeedsPersonPageUrl(personId), '_self');

  const onClickUpdateContactDetails = () => navigate(toContactDetailsEdit(propertyId, personId));

  const renderAttributeList = (list: PersonAttributeItem[]) =>
    list.length ? (
      <InfoList>
        {list
          .map((item) => referenceListDisplayValue('PERSON_ATTRIBUTE', item.attributeCode))
          .sort()
          .map((value) => (
            <li key={value}>{value}</li>
          ))}
      </InfoList>
    ) : undefined;

  const renderContactDetailList = (list: ContactDetailsItem[]) =>
    list.length ? (
      <InfoList>
        {[...new Set(list.map(({ value }) => value.trim()).sort())].map((value) => (
          <li key={value}>{value}</li>
        ))}
      </InfoList>
    ) : undefined;

  return (
    <BodyBox>
      <PageTitle title={name} />
      <PageBreadcrumbs links={[...getPropertyBreadcrumbs(property), { name }]} />
      <ItemDetails
        titleLabel={titleLabel(isTenant, isPendingMember)}
        title={name}
        actions={
          isHistoric || isTenant
            ? undefined
            : [
                {
                  key: 'end-household-member',
                  children: itemActionLabel('ui/cross-circle', 'End household member'),
                  disabled: isPendingMember,
                  onClick: showEndMemberDialog,
                },
              ]
        }
        information={
          isHistoric
            ? [
                [
                  personFieldDisplayValues.householdMemberEnded,
                  formatDMY(person.householdMemberEnded ?? ''),
                ],
              ]
            : undefined
        }
        sections={[
          {
            title: 'Personal information',
            actions: isHistoric
              ? undefined
              : [
                  {
                    key: 'update',
                    children: itemActionLabel('ui/edit-2', 'Update information'),
                    disabled: isPendingMember,
                    onClick: onClickUpdate,
                  },
                ],
            details: [
              [personFieldDisplayValues.fullName, name],
              [personFieldDisplayValues.initials, person.initials],
              [
                personFieldDisplayValues.otherName,
                formatOrFallback(person.otherName, titleCaseFromCaps, ''),
              ],
              [personFieldDisplayValues.dateOfBirth, formatDMY(person.dateOfBirth)],
              !isTenant && [
                personFieldDisplayValues.relationToTenant,
                referenceListDisplayValue('RELATION', person.relationToTenant),
              ],
              [
                personFieldDisplayValues.genderAtBirth,
                referenceListDisplayValue('GENDER_AT_BIRTH', person.genderAtBirth),
              ],
              [personFieldDisplayValues.gender, referenceListDisplayValue('SEX', person.gender)],
              [
                personFieldDisplayValues.maritalStatus,
                referenceListDisplayValue('MAR_STAT', person.maritalStatus),
              ],
              [
                personFieldDisplayValues.economicStatus,
                referenceListDisplayValue('ECONOMIC_STATUS', person.economicStatus),
              ],
              [
                personFieldDisplayValues.ethnicOrigin,
                referenceListDisplayValue('ETHNIC2', person.ethnicOrigin),
              ],
              [
                personFieldDisplayValues.language,
                referenceListDisplayValue('SUPPORTED_NLD', person.language),
              ],
              [
                personFieldDisplayValues.sexuality,
                referenceListDisplayValue('SEXUAL_ORIENTATION', person.sexuality),
              ],
              [
                personFieldDisplayValues.religion,
                referenceListDisplayValue('RELIGION', person.religion),
              ],
              [
                personFieldDisplayValues.preferredMethodOfContact,
                referenceListDisplayValue('PREF_COMM', person.preferredMethodOfContact),
              ],
            ].filter(Boolean) as InfoItem[],
          },
          {
            title: 'Additional needs',
            actions: isHistoric
              ? undefined
              : [
                  {
                    key: 'update-support-needs',
                    children: itemActionLabel('ui/edit-2', 'Open in Support Needs app'),
                    disabled: isPendingMember,
                    onClick: onClickOpenSupportNeeds,
                  },
                ],
            details: [
              [personFieldDisplayValues.supportNeeds, renderAttributeList(person.supportNeeds)],
              [
                personFieldDisplayValues.reasonableAdjustments,
                renderAttributeList(person.reasonableAdjustments),
              ],
              [
                personFieldDisplayValues.communicationAdjustments,
                renderAttributeList(person.communicationAdjustments),
              ],
              [
                personFieldDisplayValues.communicationPreferences,
                renderAttributeList(person.communicationPreferences),
              ],
            ],
          },
          {
            title: 'PCFRA details',
            details: [
              [
                personFieldDisplayValues.pcfraStatus,
                referenceListDisplayValue('PCFRA_STATUS', person.pcfraStatus),
              ],
              [
                personFieldDisplayValues.pcfraStatusComments,
                // oxlint-disable-next-line react/jsx-key -- prop tuple, not rendered children
                <PcfraStatusComments>
                  <Markdown>{person.pcfraStatusComments}</Markdown>
                </PcfraStatusComments>,
              ],
              [
                personFieldDisplayValues.pcfraReviewDate,
                formatOrFallback(person.pcfraReviewDate, formatDMY, ''),
              ],
            ],
          },
          {
            title: 'Contact information',
            actions: isHistoric
              ? undefined
              : [
                  {
                    key: 'update-contact-details',
                    children: itemActionLabel('ui/edit-2', 'Update contact details'),
                    disabled: isPendingMember,
                    onClick: onClickUpdateContactDetails,
                  },
                ],
            details: [
              [
                personFieldDisplayValues.emailAddresses,
                renderContactDetailList(person.emailAddresses),
              ],
              [
                personFieldDisplayValues.mobileNumbers,
                renderContactDetailList(person.mobileNumbers),
              ],
              [
                personFieldDisplayValues.smsOnlyNumbers,
                renderContactDetailList(person.smsOnlyNumbers),
              ],
              [
                personFieldDisplayValues.otherPhoneNumbers,
                renderContactDetailList(person.otherPhoneNumbers),
              ],
            ],
          },
        ]}
      />

      {endMemberDialog}
    </BodyBox>
  );
};
